import * as React from "react";
import {
  Backdrop,
  Dialog,
  LinearProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import WebViewer from "@pdftron/pdfjs-express-viewer";
import { useEffect, useRef } from "react";

export const PdfViewer = (props) => {
  const {
    fileUrl,
    openInvoice,
    invoiceHtmlTitle,
    handleCloseInvoice,
    invoiceTitle,
    invoiceHtml,
    handleClickOpenSendEmail,
    itemData,
    handleDownload,
    isforSmallDevice,
    isLoading,
  } = props;
  const theme = useTheme();

  const viewer = useRef(null);
  const instanceRef = useRef<any>(null);

  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [loading, setLoading] = React.useState(true);

  function base64ToBlob(base64) {
    let data = base64.split(",");
    const binaryString = window.atob(data[1]);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; ++i) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new Blob([bytes], { type: "application/pdf" });
  }

  // Safely tear down a WebViewer instance so its WASM/worker/canvas memory is released
  function disposeInstance() {
    const instance = instanceRef.current;
    if (!instance) return;
    try {
      instance.UI?.dispose?.();
    } catch (e) {
      console.warn("WebViewer UI dispose failed:", e);
    }
    try {
      instance.dispose?.();
    } catch (e) {
      console.warn("WebViewer dispose failed:", e);
    }
    instanceRef.current = null;
  }

  useEffect(() => {
    // Always clean up whatever instance existed before this run
    disposeInstance();

    if (viewer.current != null && invoiceHtml !== "") {
      setLoading(true);
      WebViewer(
        {
          path: "/webviewer/lib",
          licenseKey: "odz1p7zRg12CVHoDzBOY",
          disabledElements: [
            "searchButton",
            "menuButton",
            "selectToolButton",
            "viewControlsButton",
            "textSelectButton",
            "applyWatermark",
            "moreButton",
            "panToolButton",
          ],
          css: "/pdfstyle.css",
        },
        viewer.current,
      ).then((instance) => {
        instanceRef.current = instance;

        // now you can access APIs through the WebViewer instance
        const { Core, UI } = instance;
        var FitMode = instance.UI.FitMode;
        instance.UI.useEmbeddedPrint(true);
        instance.UI.setFitMode(FitMode.FitWidth);
        instance.UI.loadDocument(base64ToBlob(invoiceHtml), {
          filename: invoiceTitle,
        });
        // adding an event listener for when a document is loaded
        Core.documentViewer.addEventListener("documentLoaded", () => {
          setLoading(false);
        });

        // adding an event listener for when the page number has changed
        Core.documentViewer.addEventListener(
          "pageNumberUpdated",
          (pageNumber) => {
            // console.log(`Page number is: ${pageNumber}`);
          },
        );
        // adds a button to the header that on click sets the page to the next page
        instance.UI.setHeaderItems((header) => {
          header.getHeader("default").push({
            img: "icon-header-print-line",
            index: -1,
            type: "actionButton",
            element: "printButton",
            onClick: () => {
              instance.UI.print();
            },
          });
          header.getHeader("default").push({
            img: "icon-header-download",
            index: -1,
            type: "actionButton",
            element: "downloadButton",
            onClick: () => {
              if (isforSmallDevice) {
                handleDownload();
              } else {
                instance.UI.downloadPdf();
              }
            },
          });
          if (
            !["EWAYBILLORIGINAL", "EWAYBILLDRIVERCOPY", "EINVOICE"].includes(
              props?.fileName,
            )
          )
            header.getHeader("default").push({
              img: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="m0 0h24v24h-24z" fill="none"></path><path d="m19 6.41-1.41-1.41-5.59 5.59-5.59-5.59-1.41 1.41 5.59 5.59-5.59 5.59 1.41 1.41 5.59-5.59 5.59 5.59 1.41-1.41-5.59-5.59z"></path></svg>',
              index: -1,
              type: "actionButton",
              element: "defaultHeaderButton",
              onClick: () => {
                handleCloseInvoice();
              },
            });
        });
      });
    } else {
      setLoading(false);
    }

    // Cleanup on unmount or before the effect re-runs for a new invoiceHtml
    return () => {
      disposeInstance();
    };
  }, [invoiceHtml, viewer.current]);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={openInvoice}
      onClose={handleCloseInvoice}
      maxWidth="md"
      fullWidth={true}
    >
      {(loading || isLoading) && (
        <div
          style={{
            width: "35%",
            height: "100vh",
            left: "50%",
            top: "100%",
            position: "fixed",
            transform: "translate(-50%, -50%)",
          }}
        >
          <LinearProgress color="primary" />
        </div>
      )}
      <div style={{ height: "100vh" }} className="webviewer" ref={viewer}></div>
    </Dialog>
  );
};
